import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Lock, Mail, Phone, Clock, CheckCircle } from "lucide-react";
import "../styles/PrivacyPage.css";

export default function PrivacyPage() {
  return (
    <div className="privacy-page-container">
      {/* Header Hero */}
      <header className="privacy-hero-section">
        <Link to="/" className="privacy-back-button">
          <ArrowLeft size={20} />
          Retour à l'accueil
        </Link>
        
        <h1 className="privacy-hero-title">Politique de Confidentialité</h1>
        <p className="privacy-hero-subtitle">Protection et transparence de vos données personnelles</p>
        
        <div className="privacy-last-updated">
          <Shield size={16} />
          Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
        </div>
      </header>

      {/* Main Content */}
      <main className="privacy-content-section">
        {/* Introduction */}
        <section className="privacy-section-block">
          <h2 className="privacy-section-title">1. Introduction</h2>
          <p className="privacy-section-text">
            UltimaTag ("nous", "notre", "nos") s'engage à protéger la confidentialité 
            de vos données personnelles. Cette politique explique comment nous collectons, 
            utilisons et protégeons vos informations.
          </p>
          <p className="privacy-section-text">
            En utilisant UltimaTag, vous acceptez cette politique. Nous respectons 
            scrupuleusement le RGPD et les lois ivoiriennes sur la protection des données.
          </p>
        </section>

        {/* Informations collectées */}
        <section className="privacy-section-block">
          <h2 className="privacy-section-title">2. Informations Collectées</h2>
          
          <h3 className="privacy-section-subtitle">Informations que vous nous fournissez :</h3>
          <ul className="privacy-list">
            <li className="privacy-list-item"><strong>Identité</strong> : Nom, prénom, email, téléphone</li>
            <li className="privacy-list-item"><strong>Paiement</strong> : Données de mobile money (sécurisées PCI DSS)</li>
            <li className="privacy-list-item"><strong>Contact d'urgence</strong> : Personnes à prévenir</li>
            <li className="privacy-list-item"><strong>Objets protégés</strong> : Descriptions, nom de l'objet</li>
          </ul>

          <h3 className="privacy-section-subtitle">Informations collectées automatiquement :</h3>
          <ul className="privacy-list">
            <li className="privacy-list-item"><strong>Utilisation</strong> : Pages visitées, fonctionnalités utilisées</li>
            <li className="privacy-list-item"><strong>Techniques</strong> : IP, appareil, navigateur, OS</li>
            {/* <li className="privacy-list-item"><strong>Localisation</strong> : Uniquement pour objets perdus/retrouvés</li> */}
          </ul>

          <div className="privacy-highlight-box">
            <Lock size={20} style={{ marginRight: '0.5rem' }} />
            <strong>Principe de minimisation :</strong> Nous ne collectons que le nécessaire.
          </div>
        </section>

        {/* Utilisation des données */}
        <section className="privacy-section-block">
          <h2 className="privacy-section-title">3. Utilisation des Données</h2>
          <p className="privacy-section-text">Vos données servent à :</p>
          <ul className="privacy-list">
            <li className="privacy-list-item">Fournir et améliorer UltimaTag</li>
            <li className="privacy-list-item">Vous alerter lors de la récupération d'objets</li>
            <li className="privacy-list-item">Traiter vos commandes de stickers</li>
            <li className="privacy-list-item">Envoyer des mises à jour importantes</li>
            <li className="privacy-list-item">Prévenir la fraude et les abus</li>
            <li className="privacy-list-item">Respecter nos obligations légales</li>
          </ul>
        </section>

        {/* Partage des données */}
        <section className="privacy-section-block">
          <h2 className="privacy-section-title">4. Partage des Données</h2>
          <p className="privacy-section-text">Nous ne vendons pas vos données. Partage uniquement dans ces cas :</p>
          
          <h3 className="privacy-section-subtitle">Avec votre consentement :</h3>
          <p className="privacy-section-text">Coordonnées partagées uniquement avec votre accord explicite.</p>

          <h3 className="privacy-section-subtitle">Pour le service :</h3>
          <ul className="privacy-list">
            <li className="privacy-list-item">Processeurs de paiement sécurisés</li>
            <li className="privacy-list-item">Services d'envoi SMS/emails</li>
            <li className="privacy-list-item">Partenaires de livraison</li>
            <li className="privacy-list-item">Points relais (informations minimales)</li>
          </ul>

          <h3 className="privacy-section-subtitle">Obligations légales :</h3>
          <p className="privacy-section-text">En réponse à des demandes légales valides.</p>
        </section>

        {/* Sécurité */}
        <section className="privacy-section-block">
          <h2 className="privacy-section-title">5. Sécurité des Données</h2>
          <p className="privacy-section-text">Nous protégeons vos données avec :</p>
          <ul className="privacy-list">
            <li className="privacy-list-item">Chiffrement HTTPS/TLS en transit</li>
            <li className="privacy-list-item">Chiffrement des données au repos</li>
            <li className="privacy-list-item">Contrôles d'accès stricts</li>
            <li className="privacy-list-item">Audits de sécurité réguliers</li>
            <li className="privacy-list-item">Sauvegardes sécurisées</li>
          </ul>
          
          <div className="privacy-highlight-box">
            <Shield size={20} style={{ marginRight: '0.5rem' }} />
            <strong>Transparence :</strong> En cas de violation, nous vous informerons sous 72h.
          </div>
        </section>

        {/* Droits des utilisateurs */}
        <section className="privacy-section-block">
          <h2 className="privacy-section-title">6. Vos Droits</h2>
          <p className="privacy-section-text">Conformément au RGPD, vous avez droit à :</p>
          
          <div className="privacy-rights-grid">
            <div className="privacy-right-card">
              <div className="ok">
              <CheckCircle size={20} color="var(--color-green)"  />
              <strong>Accès</strong>
              </div>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem', color: 'var(--color-text-light)' }}>
                Copie de vos données
              </p>
            </div>
            
            <div className="privacy-right-card">
             <div className="ok">
             <CheckCircle size={20} color="var(--color-green)"/>
             <strong>Rectification</strong>
             </div>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem', color: 'var(--color-text-light)' }}>
                Correction des erreurs
              </p>
            </div>
            
            <div className="privacy-right-card">
             <div className="ok">
             <CheckCircle size={20} color="var(--color-green)" />
             <strong>Effacement</strong>
             </div>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem', color: 'var(--color-text-light)' }}>
                Droit à l'oubli
              </p>
            </div>
            
            <div className="privacy-right-card">
              <div className="ok">
              <CheckCircle size={20} color="var(--color-green)"  />
              <strong>Portabilité</strong>
              </div>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem', color: 'var(--color-text-light)' }}>
                Données exportables
              </p>
            </div>
          </div>
        </section>

        {/* Conservation */}
        <section className="privacy-section-block">
          <h2 className="privacy-section-title">7. Conservation des Données</h2>
          <div className="privacy-highlight-box">
            <Clock size={20} style={{ marginRight: '0.5rem' }} />
            <strong>Durées de conservation :</strong>
            <ul className="privacy-list" style={{ marginTop: '0.5rem' }}>
              <li className="privacy-list-item"><strong>Compte actif</strong> : 3 ans après fermeture</li>
              <li className="privacy-list-item"><strong>Transactions</strong> : 10 ans (obligation légale)</li>
              <li className="privacy-list-item"><strong>Objets</strong> : 5 ans après dernière activité</li>
            </ul>
          </div>
        </section>

        {/* Contact DPO */}
        <div className="privacy-contact-section">
          <h3 className="privacy-contact-title">Contactez notre service</h3>
          <p className="privacy-section-text" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Pour toute question ou exercice de vos droits
          </p>
          
          <div className="privacy-contact-grid">
            <div className="privacy-contact-card">
              <div className="privacy-contact-icon">
                <Mail size={24} />
              </div>
              <h4 className="privacy-contact-card-title">Email</h4>
              <a href="mailto:support@ultimatag.com" className="privacy-email-link">
                support@ultimatag.com
              </a>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem', opacity: '0.9' }}>
                Réponse sous 30 jours
              </p>
            </div>
            
            <div className="privacy-contact-card">
              <div className="privacy-contact-icon">
                <Phone size={24} />
              </div>
              <h4 className="privacy-contact-card-title">Téléphone</h4>
              <div className="privacy-email-link">+225 07 12 20 49 86</div>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem', opacity: '0.9' }}>
                Lundi-Vendredi, 8h-17h
              </p>
            </div>
          </div>
        </div>

        {/* Modifications */}
        <section className="privacy-section-block">
          <h2 className="privacy-section-title">8. Modifications</h2>
          <p className="privacy-section-text">
            Nous mettrons à jour cette politique périodiquement. 
            Les changements significatifs seront notifiés par email.
          </p>
          <p className="privacy-section-text">
            Consultez régulièrement cette page pour rester informé.
          </p>
        </section>
      </main>

      {/* Footer */}
      <footer className="privacy-footer-section">
        <p>
          © {new Date().getFullYear()} UltimaTag. Tous droits réservés.
        </p>
        <p style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
          Conforme au RGPD et à la loi ivoirienne n°2013-450
        </p>
        
      </footer>
    </div>
  );
}